import { Badge } from '@any-ui-react/core';

import { EMPTY_DEFAULT } from '@/common/components/shared/table';
import { ProbationReviewFlag, ProbationReviewOutcome, ProbationStatus } from '@/common/graphql';
import { ProbationReviewFlagLabels, ProbationReviewOutcomeLabels, ProbationStatusLabels } from '@/common/types';

const statusColor: Record<ProbationStatus, string> = {
  [ProbationStatus.NotStarted]: 'gray',
  [ProbationStatus.InProgress]: 'blue',
  [ProbationStatus.Over]: 'green',
};

const flagColor: Record<ProbationReviewFlag, string> = {
  [ProbationReviewFlag.Overdue]: 'red',
  [ProbationReviewFlag.DueSoon]: 'orange',
  [ProbationReviewFlag.Extended]: 'yellow',
};

interface StatusProps {
  probationStatus?: ProbationStatus | null;
  outcome?: ProbationReviewOutcome | null;
}

interface FlagProps {
  flags?: ProbationReviewFlag[] | null;
  /** A Draft review whose reviewer has left. */
  reviewerAbsent?: boolean;
}

/** The Probation Status, and the Outcome once it is recorded. */
export const ProbationStatusBadge = ({ probationStatus, outcome }: StatusProps) => {
  if (!probationStatus) {
    return <span>{EMPTY_DEFAULT}</span>;
  }

  return (
    <div className="inline-flex flex-wrap items-center gap-1">
      <Badge color={statusColor[probationStatus]}>{ProbationStatusLabels[probationStatus]}</Badge>
      {outcome && (
        <Badge color={outcome === ProbationReviewOutcome.Passed ? 'green' : 'red'}>
          {ProbationReviewOutcomeLabels[outcome]}
        </Badge>
      )}
    </div>
  );
};

/** A Draft review whose reviewer has left (not a flag): shown in the list and under the Reviewer of the review. */
export const ReviewerAbsentBadge = () => <Badge color="gray">Reviewer absent</Badge>;

/** The automatic flags (Overdue, Due Soon, Extended) and Reviewer absent; a placeholder when there are none. */
export const ProbationFlagBadges = ({ flags, reviewerAbsent }: FlagProps) => {
  if (!flags?.length && !reviewerAbsent) {
    return <span>{EMPTY_DEFAULT}</span>;
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-1">
      {flags?.map(flag => (
        <Badge key={flag} color={flagColor[flag]}>
          {ProbationReviewFlagLabels[flag]}
        </Badge>
      ))}
      {reviewerAbsent && <ReviewerAbsentBadge />}
    </div>
  );
};

/** Status, outcome and flags together, for places that have room for one cell only. */
export const ProbationReviewStatusBadge = ({
  probationStatus,
  outcome,
  flags,
  reviewerAbsent,
}: StatusProps & FlagProps) => {
  if (!probationStatus) {
    return <span>{EMPTY_DEFAULT}</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      <ProbationStatusBadge outcome={outcome} probationStatus={probationStatus} />
      {(flags?.length || reviewerAbsent) && <ProbationFlagBadges flags={flags} reviewerAbsent={reviewerAbsent} />}
    </div>
  );
};
